import React from 'react';
import { Wifi, WifiOff, Battery, RefreshCw, LogOut, Sun, Moon } from 'lucide-react';
import { Driver, SystemConfig, ThemeMode } from '../../../types';
import { DriverPerformanceModal } from '../DriverPerformanceModal';

interface Props {
    config: SystemConfig;
    driver: Driver;
    activeOrdersCount: number;
    isOnline: boolean;
    onLogout: () => void;
    driverTheme: ThemeMode;
    onToggleTheme: () => void;
    isAdmin?: boolean;
    onBackToDispatcher?: () => void;
    // v60.3: Entregas do dia calculadas corretamente
    todaysDeliveries?: number;
}

export const DriverHeader: React.FC<Props> = ({ config, driver, activeOrdersCount, isOnline, onLogout, driverTheme, onToggleTheme, isAdmin, onBackToDispatcher, todaysDeliveries }) => {
    const [isPerformanceOpen, setIsPerformanceOpen] = React.useState(false);
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);

    // v60.4: Abreviar nome para caber em telas pequenas
    const firstName = driver.name.split(' ')[0];

    return (
        <>
            <div className="bg-card-bg/95 backdrop-blur-xl border-b border-border-dim text-text-primary p-3 sticky top-0 z-50 shadow-lg">
                <div className="flex justify-between items-center max-w-3xl mx-auto">
                    {/* LADO ESQUERDO: Info do Driver */}
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="relative group shrink-0">
                            <div className="w-10 h-10 bg-card-bg border-2 border-sal-cyber-teal rounded-xl flex items-center justify-center text-sal-cyber-teal font-black text-xl shadow-md group-hover:scale-105 transition-transform">
                                {driver.name.charAt(0)}
                            </div>
                            <div className="absolute -bottom-0.5 -right-1 flex gap-1">
                                <div className={`w-4 h-4 rounded-full border-2 border-card-bg flex items-center justify-center ${isOnline ? 'bg-status-ready' : 'bg-status-critical'}`}>
                                    {isOnline ? <Wifi className="w-2 h-2 text-badge-text" /> : <WifiOff className="w-2 h-2 text-badge-text" />}
                                </div>
                            </div>
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="font-black text-lg tracking-tight leading-none uppercase truncate">{firstName}</h2>
                            <div className="flex items-center gap-2 text-[10px] text-text-muted mt-1 font-black uppercase tracking-wider flex-wrap">
                                {/* Bateria - só ícone quando baixa */}
                                <span className="flex items-center gap-0.5 shrink-0">
                                    <Battery className={`w-3 h-3 ${driver.battery < 20 ? 'text-status-critical animate-pulse' : 'text-status-ready'}`} />
                                    <span className={driver.battery < 20 ? 'text-status-critical' : ''}>{driver.battery}%</span>
                                </span>
                                <span className="w-0.5 h-0.5 bg-border-dim rounded-full shrink-0"></span>
                                <span className="text-sal-cyber-teal shrink-0">{activeOrdersCount} Pendentes</span>
                                <span className="w-0.5 h-0.5 bg-border-dim rounded-full shrink-0"></span>
                                <button
                                    type="button"
                                    onClick={() => setIsPerformanceOpen(true)}
                                    className="flex items-center gap-0.5 hover:bg-black/5 dark:hover:bg-white/10 px-1 rounded transition-colors cursor-pointer shrink-0"
                                >
                                    <span className="text-status-ready">{todaysDeliveries || 0} Feitas</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* LADO DIREITO: Botões - compactados */}
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        {/* Tema - compacto */}
                        <button
                            type="button"
                            onClick={onToggleTheme}
                            className={`p-2 rounded-xl active:scale-90 border transition-all ${driverTheme === 'solar' ? 'bg-status-warning/10 text-status-warning border-status-warning/30' : 'bg-black/5 dark:bg-white/5 text-icon-muted border-border-dim'}`}
                            aria-label={driverTheme === 'solar' ? 'Modo escuro' : 'Modo solar'}
                        >
                            {driverTheme === 'solar' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                        </button>

                        {/* Admin - só ícone */}
                        {isAdmin && onBackToDispatcher && (
                            <button 
                                type="button" 
                                onClick={onBackToDispatcher} 
                                className="p-2 bg-status-info/10 rounded-xl hover:bg-status-info/20 active:scale-90 text-status-info border border-status-info/20 transition-all"
                                aria-label="Voltar ao painel"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        )}

                        {/* Menu de 3 pontos para ações secundárias */}
                        <div className="relative">
                            <button 
                                type="button"
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="p-2 bg-black/5 dark:bg-white/5 rounded-xl hover:bg-black/10 dark:hover:bg-white/10 active:scale-90 text-icon-muted border border-border-dim transition-all"
                                aria-label="Mais opções"
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="6" r="2" />
                                    <circle cx="12" cy="12" r="2" />
                                    <circle cx="12" cy="18" r="2" />
                                </svg>
                            </button>

                            {/* Dropdown Menu */}
                            {isMenuOpen && (
                                <div className="absolute right-0 top-full mt-2 w-40 bg-card-bg border border-border-dim rounded-xl shadow-xl z-50 py-1 animate-in fade-in slide-in-from-top-2 duration-200">
                                    <button
                                        type="button"
                                        onClick={() => { window.location.reload(); setIsMenuOpen(false); }}
                                        className="w-full px-3 py-2.5 text-left text-xs font-black uppercase tracking-wider text-text-secondary hover:bg-white/5 flex items-center gap-2 transition-colors"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                        Atualizar App
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { onLogout(); setIsMenuOpen(false); }}
                                        className="w-full px-3 py-2.5 text-left text-xs font-black uppercase tracking-wider text-status-critical hover:bg-white/5 flex items-center gap-2 transition-colors"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        Sair
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Click outside para fechar menu */}
            {isMenuOpen && (
                <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
            )}

            <DriverPerformanceModal
                driver={driver}
                config={config}
                isOpen={isPerformanceOpen}
                onClose={() => setIsPerformanceOpen(false)}
                todaysDeliveries={todaysDeliveries || 0}
            />
        </>
    );
};
